import { Descriptions } from "@/components/v2";
import { sources } from "@/lib/formatter/dossiers";
import { type DescriptionsProps } from "antd";
import {
  type Dossier,
  isRepairDirectGenericDossier,
} from "@rumble-it/common-lib/types/models/Dossier.interface";
import { format, parseISO } from "date-fns";
import { Card } from "@rumbledirect/ui";

interface DossierCardProps {
  dossier: Dossier | undefined;
  isLoading?: boolean;
}

export const DossierCard = ({ dossier, isLoading }: DossierCardProps) => {
  const items: DescriptionsProps["items"] = [
    {
      label: "Aangemaakt op",
      children: dossier?.createdAt
        ? format(parseISO(dossier.createdAt), "dd-MM-yyyy HH:mm")
        : null,
    },
    {
      label: "Aangemaakt via",
      children: dossier?.source ? (sources[dossier.source] ?? null) : null,
    },
    {
      label: "Schadeoorzaak",
      children: dossier?.causeOfDamage?.label || dossier?.causeOfDamage,
    },
    {
      label: "Type schade",
      children: dossier?.typeOfDamage?.label || dossier?.typeOfDamage,
    },
    {
      label: "Schadedatum",
      children: dossier?.damageAt
        ? (() => {
            const date = parseISO(dossier.damageAt);
            const time = format(date, "HH:mm");
            return time === "00:00"
              ? format(date, "dd-MM-yyyy")
              : format(date, "dd-MM-yyyy HH:mm");
          })()
        : null,
    },
    {
      label: "Schade aangrijpingspunt",
      children:
        dossier?.damageFromDirection?.label || dossier?.damageFromDirection,
    },
    {
      label: "Eigenaar auto",
      children: dossier?.driverType?.label || dossier?.driverType,
    },
    {
      label: "Verzekeraar",
      children: dossier?.insurerName || dossier?.insuranceCompany?.fullName,
    },
    {
      label: "Leasemaatschappij",
      children: dossier?.leaseCompany?.fullName,
    },
    {
      label: "Polisnummer",
      children: dossier?.polisNumber,
    },
    {
      label: "Schadenummer",
      children: dossier?.damageNumber,
    },
    {
      label: "Rijdbaar",
      children:
        dossier?.driveable == true
          ? "Ja"
          : dossier?.driveable == false
            ? "Nee"
            : null,
    },
    {
      label: "Herstel aangevraagd",
      children:
        dossier?.repairRequested === true
          ? "Ja"
          : dossier?.repairRequested === false
            ? "Nee"
            : null,
    },
    {
      label: "Weggesleept",
      children:
        dossier?.towedAway == true
          ? "Ja"
          : dossier?.towedAway == false
            ? "Nee"
            : null,
    },
    {
      label: "Schade gemeld",
      children:
        dossier?.damageReported === true
          ? "Ja"
          : dossier?.damageReported === false
            ? "Nee"
            : null,
    },
    {
      label: "Vervangend vervoer",
      children:
        dossier?.replacementVehicleType?.label ||
        dossier?.replacementVehicleType ||
        (dossier?.replacementVehicle === true
          ? "Ja"
          : dossier?.replacementVehicle === false
            ? "Nee"
            : null),
    },
    {
      label: "Opmerking bestuurder",
      children: dossier?.commentFromDriver,
    },
    {
      label: "Overige opmerkingen",
      children: dossier?.additionalComment,
    },
    {
      label: "Andere partij betrokken",
      children:
        dossier?.otherPartyInvolved?.label || dossier?.otherPartyInvolved,
    },
    {
      label: "Afwijkende bestuurder",
      children:
        dossier?.isDeviatingDriver === true
          ? "Ja"
          : dossier?.isDeviatingDriver === false
            ? "Nee"
            : null,
    },
    {
      label: "ImageCapture",
      children:
        dossier?.imageCapture === true
          ? "Ja"
          : dossier?.imageCapture === false
            ? "Nee"
            : null,
    },
    {
      label: "Betaalwijze",
      children: dossier?.paymentByType?.label || dossier?.paymentByType,
    },
    {
      label: "Volmacht",
      children: dossier?.mandate,
    },
    {
      label: "Haal- en brengservice",
      children:
        dossier?.pickupService === true
          ? "Ja"
          : dossier?.pickupService === false
            ? "Nee"
            : null,
    },
    {
      label: "Schade verhaalbaar",
      children:
        dossier && isRepairDirectGenericDossier(dossier)
          ? dossier.damageClaimable === true
            ? "Ja"
            : dossier.damageClaimable === false
              ? "Nee"
              : null
          : null,
    },
    {
      label: "Wie heeft schade",
      children:
        dossier && isRepairDirectGenericDossier(dossier)
          ? dossier.damagedParty?.label || dossier.damagedParty
          : null,
    },
    {
      label: "Aansprakelijke partij",
      children:
        dossier && isRepairDirectGenericDossier(dossier)
          ? dossier.responsibleParty?.label || dossier.responsibleParty
          : null,
    },
    {
      label: "Letsel inzittenden",
      children:
        dossier && isRepairDirectGenericDossier(dossier)
          ? dossier.personalInjury === true
            ? "Ja"
            : dossier.personalInjury === false
              ? "Nee"
              : null
          : null,
    },
    {
      label: "Proces verbaal opgemaakt?",
      children:
        dossier && isRepairDirectGenericDossier(dossier)
          ? dossier.hasPoliceReport === true
            ? "Ja"
            : dossier.hasPoliceReport === false
              ? "Nee"
              : null
          : null,
    },
    {
      label: "Type herstel",
      children:
        dossier && isRepairDirectGenericDossier(dossier)
          ? (dossier.repairType?.label ?? dossier.repairType ?? "Standaard")
          : null,
    },
    {
      label: "Type verzekering",
      children: dossier?.insuranceType?.label || dossier?.insuranceType,
    },
    {
      label: "Service adviseur naam",
      children: dossier?.serviceAdvisorName,
    },
    {
      label: "Service adviseur e-mail",
      children: dossier?.serviceAdvisorEmail ?? null,
    },
  ];

  return (
    <Card loading={isLoading} className="w-full" title="Opdrachtinformatie">
      <Descriptions items={items} />
    </Card>
  );
};
